import React, { useEffect, useState } from "react";
import { Select, Radio, Empty, Space, Divider } from "antd";
import { LoadingOutlined, PlusOutlined } from "@ant-design/icons";

const { Option } = Select;

const CustomSearchDropdown = ({
  placeholder,
  filters = [],
  initialValue,
  labelInValue = false,
  options = [],
  onFilterChange,
  onSelect,
  value,
  onChange,
  mode = "default", // 'notification' | 'description' | 'default'
  optionLabelProp = "label",
  ...selectProps
}) => {
  const [filter, setFilter] = useState(initialValue);
  const [searchValue, setSearchValue] = useState("");
  const [filteredOptions, setFilteredOptions] = useState(options);

  // Helper function to safely render ANY value as string
  const safeStringValue = (val) => {
    if (val === null || val === undefined) return "";
    if (typeof val === "string") return val;
    if (typeof val === "number") return String(val);
    if (typeof val === "boolean") return String(val);
    if (typeof val === "object") {
      // For objects, try to extract meaningful display value
      if (val.clientName) return String(val.clientName);
      if (val.clientCode) return String(val.clientCode);
      if (val.name) return String(val.name);
      if (val.label) return String(val.label);
      if (val.title) return String(val.title);
      if (val.id) return String(val.id);
      if (val.encodedKey) return String(val.encodedKey);
      // Last resort - return a safe string representation
      return "[Object]";
    }
    return String(val);
  };

  // Custom display for selected value
  const getSelectedDisplay = (currentValue) => {
    if (!currentValue) return undefined;

    // Find the actual option object
    let selectedOption;

    if (labelInValue) {
      // When labelInValue is true, value is { value: x, label: y }
      selectedOption = options.find(
        (opt) =>
          opt.value === currentValue.value ||
          safeStringValue(opt.value) === safeStringValue(currentValue.value)
      );
    } else {
      // When labelInValue is false, value is just the value
      selectedOption = options.find(
        (opt) =>
          opt.value === currentValue ||
          safeStringValue(opt.value) === safeStringValue(currentValue)
      );
    }

    if (!selectedOption) {
      // Fallback to default behavior
      return labelInValue ? currentValue.label : currentValue;
    }

    // Return display text based on mode
    if (mode === "notification") {
      return `${safeStringValue(selectedOption.label)} • ${safeStringValue(
        selectedOption.detail
      )}`;
    }

    if (mode === "description") {
      const tags = (selectedOption.tags || [])
        .slice(0, 2)
        .map((tag) => safeStringValue(tag))
        .join(", ");
      return `${safeStringValue(selectedOption.label)}${
        tags ? ` • ${tags}` : ""
      }`;
    }

    // Default mode
    return safeStringValue(selectedOption.label);
  };

  // Filter + Search
  useEffect(() => {
    let result = [...options];
    if (filter) result = result.filter((opt) => opt?.category === filter);
    if (searchValue) {
      result = result.filter((opt) => {
        const labelStr = safeStringValue(opt.label);
        const detailStr = safeStringValue(opt.detail);
        return (
          labelStr.toLowerCase().includes(searchValue.toLowerCase()) ||
          detailStr.toLowerCase().includes(searchValue.toLowerCase())
        );
      });
    }
    setFilteredOptions(result);
  }, [options, filter, searchValue]);

  const handleFilterChange = (e) => {
    setFilter(e.target.value);
    onFilterChange?.(e.target.value);
  };

  const handleSearch = (val) => setSearchValue(val);

  const handleSelect = (val, option) => {
    onSelect?.(val, option);
    onChange?.(val);
  };

  // Custom tag render for multiple mode
  const tagRender = (props) => {
    const { label, value, closable, onClose } = props;

    const selectedOption = options.find(
      (opt) =>
        opt.value === value ||
        safeStringValue(opt.value) === safeStringValue(value)
    );

    if (!selectedOption) {
      return (
        <span className="ant-select-selection-item">
          <span className="ant-select-selection-item-content">{label}</span>
        </span>
      );
    }

    if (mode === "notification") {
      return (
        <span className="ant-select-selection-item">
          <span className="ant-select-selection-item-content">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "1px 4px",
              }}
            >
              <div
                style={{
                  background: "#e2e8f0",
                  padding: "2px 8px",
                  borderRadius: 12,
                  fontSize: "12px",
                  lineHeight: "18px",
                  height: "20px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {safeStringValue(selectedOption.label)}
              </div>
              <div
                style={{
                  color: "#64748b",
                  fontSize: "12px",
                  lineHeight: "18px",
                }}
              >
                {safeStringValue(selectedOption.detail)}
              </div>
            </div>
          </span>
        </span>
      );
    }

    if (mode === "description") {
      return (
        <span className="ant-select-selection-item">
          <span className="ant-select-selection-item-content">
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 4,
                padding: "2px 4px",
              }}
            >
              <div
                style={{
                  fontSize: "12px",
                  fontWeight: 400,
                  lineHeight: "16px",
                }}
              >
                {safeStringValue(selectedOption.label)}
              </div>
              <Space size={4}>
                {(selectedOption.tags || []).slice(0, 2).map((tag, idx) => (
                  <span
                    key={idx}
                    style={{
                      background: "#f1f5f9",
                      color: "#64748b",
                      borderRadius: 10,
                      padding: "1px 6px",
                      fontSize: "10px",
                      lineHeight: "14px",
                      height: "16px",
                      display: "inline-flex",
                      alignItems: "center",
                      border: "1px solid #e2e8f0",
                    }}
                  >
                    {safeStringValue(tag)}
                  </span>
                ))}
                {(selectedOption.tags || []).length > 2 && (
                  <span
                    style={{
                      background: "#f1f5f9",
                      color: "#64748b",
                      borderRadius: 10,
                      padding: "1px 6px",
                      fontSize: "10px",
                      lineHeight: "14px",
                      height: "16px",
                      display: "inline-flex",
                      alignItems: "center",
                      border: "1px solid #e2e8f0",
                    }}
                  >
                    +{(selectedOption.tags || []).length - 2}
                  </span>
                )}
              </Space>
            </div>
          </span>
        </span>
      );
    }

    return (
      <span className="ant-select-selection-item">
        <span className="ant-select-selection-item-content">{label}</span>
      </span>
    );
  };

  // === SAFE renderers that NEVER render objects directly ===
  const renderOptionContent = (option) => {
    const displayLabel = safeStringValue(option.label);

    if (mode === "notification") {
      return (
        <div
          style={{
            display: "flex",
            gap: "12px",
            alignItems: "center",
            padding: "12px 16px",
          }}
        >
          <div
            style={{
              background: "#e2e8f0",
              padding: "4px 10px",
              borderRadius: 12,
              fontSize: 13,
              lineHeight: "18px",
              height: "22px",
              display: "flex",
              alignItems: "center",
              fontWeight: 500,
            }}
          >
            {displayLabel}
          </div>
          <div
            style={{
              color: "#64748b",
              fontSize: 14,
              lineHeight: "20px",
            }}
          >
            {safeStringValue(option.detail)}
          </div>
        </div>
      );
    }
    if (mode === "description") {
      return (
        <div style={{ padding: "12px 16px" }}>
          <div
            style={{
              fontSize: 14,
              fontWeight: 500,
              marginBottom: 8,
              lineHeight: "20px",
            }}
          >
            {displayLabel}
          </div>
          <Space size={6} wrap>
            {(option.tags || []).map((tag, idx) => (
              <span
                key={idx}
                style={{
                  background: "#f1f5f9",
                  color: "#64748b",
                  borderRadius: 12,
                  padding: "3px 8px",
                  fontSize: 12,
                  lineHeight: "16px",
                  height: "20px",
                  display: "inline-flex",
                  alignItems: "center",
                  border: "1px solid #e2e8f0",
                }}
              >
                {safeStringValue(tag)}
              </span>
            ))}
          </Space>
        </div>
      );
    }
    return <div style={{ padding: "8px 12px" }}>{displayLabel}</div>;
  };

  const dropdownRender = (menu) => (
    <div>
      {filteredOptions.length === 0 ? (
        <div style={{ padding: "16px" }}>
          <Empty
            description={searchValue ? "No matching results" : "No data"}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </div>
      ) : (
        menu
      )}

      {filters.length > 0 && (
        <>
          <Divider style={{ margin: "8px 0" }} />
          <div style={{ padding: "12px 16px" }}>
            <Radio.Group
              onChange={handleFilterChange}
              value={filter}
              size="small"
            >
              <Space>
                {filters.map((f) => (
                  <Radio key={f.value} value={f.value}>
                    {safeStringValue(f.label)}
                  </Radio>
                ))}
              </Space>
            </Radio.Group>
          </div>
        </>
      )}
    </div>
  );

  // Get the display value for the select
  const displayValue = getSelectedDisplay(value);
  const isLoading =
    selectProps.loading ||
    (filteredOptions.length === 0 &&
      value !== undefined &&
      value !== null &&
      value !== "");

  return (
    <Select
      showSearch
      // value={value}
      value={isLoading ? undefined : value}
      loading={isLoading}
      placeholder={placeholder}
      onSearch={handleSearch}
      onSelect={handleSelect}
      onChange={onChange}
      dropdownRender={dropdownRender}
      dropdownAlign={{ points: ["tl", "bl"] }}
      getPopupContainer={(trigger) => trigger.parentNode}
      suffixIcon={
        selectProps.loading ? <LoadingOutlined spin /> : <PlusOutlined />
      }
      filterOption={false}
      optionLabelProp={labelInValue ? "label" : optionLabelProp}
      labelInValue={labelInValue}
      tagRender={selectProps.mode === "multiple" ? tagRender : undefined}
      {...selectProps}
    >
      {filteredOptions.map((option, index) => {
        // Create absolutely safe key - stringify objects
        let keyValue;
        if (typeof option.value === "object" && option.value !== null) {
          keyValue = JSON.stringify(option.value);
        } else {
          keyValue = option.value || index;
        }

        // For the option value, we need to handle labelInValue properly
        const optionValue = labelInValue ? option : option.value;

        return (
          <Option
            key={keyValue}
            value={optionValue}
            label={getSelectedDisplay(optionValue)}
            // label={safeStringValue(option.label)}
          >
            {renderOptionContent(option)}
          </Option>
        );
      })}
    </Select>
  );
};

export default CustomSearchDropdown;
